import { DocumentNode, gql } from '@apollo/client';
import { Button, useDisclosure } from '@chakra-ui/react';
import * as React from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EGroupFindPermission, EUserRole } from '@/graphql-types/globalTypes';
import { useCurrent } from '@/hooks/useCurrent';
import { useEditCurrentUserGroupMembership } from '@/hooks/useEditCurrentUserGroupMembership/useEditCurrentUserGroupMembership';
import { useSignUpPrompt } from '@/hooks/useSignUpPrompt';
import { Loading } from '@/imports/ui/chakra/components/Loading';

import { JoinButton_GroupFragment } from './graphql-types/JoinButton_GroupFragment';

// Define the Group interface by spreading JoinButton_GroupFragment and adding settings
export interface Group extends JoinButton_GroupFragment {
  settings?: {
    isAutomated?: boolean;
    findPermission?: EGroupFindPermission | null;
    // Add other settings fields here if needed
  };
}

export const JoinButton: React.FC<{
  group: Group;
}> & { groupFragment: DocumentNode } = ({ group }) => {
  const current = useCurrent();

  const {
    createFollow,
    createFollowLoading,
    deleteFollow,
    deleteFollowLoading,
    userLoading,
  } = useEditCurrentUserGroupMembership(group);

  const { promptModal, loading, showPrompt } = useSignUpPrompt();
  const confirmDialog = useDisclosure();

  const onClickJoin = async (): Promise<void> => {
    if (!current.user?._id) {
      showPrompt();
      return;
    }

    await createFollow();
  };

  if (loading || current.loading || userLoading) {
    return <Loading />;
  }

  const canJoin = group.currentUserGroupPermission?.canJoin ?? false;
  const isAdminOrMorgoth =
    current.user?.role === EUserRole.ADMIN ||
    current.user?.role === EUserRole.MORGOTH;
  const isGroupMembersOnly =
    group.settings?.findPermission === EGroupFindPermission.GROUP_MEMBERS_ONLY;
  const shouldForceDisableJoin =
    isAdminOrMorgoth && isGroupMembersOnly && !group.currentUserGroupLink?._id;

  return !!group.currentUserGroupLink?._id ? (
    <>
      <Button
        onClick={confirmDialog.onOpen}
        size="sm"
        colorScheme="gray"
        isLoading={deleteFollowLoading}
      >
        Joined
      </Button>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        onConfirm={deleteFollow}
        confirmButtonText="Leave"
        title={`Are you sure you want to leave ${group.name}?`}
        body={
          group.subgroups.totalCount > 0
            ? 'Since this group has subgroups, you will automatically leave all its subgroups too.'
            : ''
        }
      />
    </>
  ) : (canJoin && !shouldForceDisableJoin) || !current.user?._id ? (
    <>
      {promptModal}
      <Button
        disabled={!!group?.settings?.isAutomated}
        onClick={onClickJoin}
        size="sm"
        colorScheme="gray"
        variant="outline"
        isLoading={createFollowLoading}
      >
        Join
      </Button>
    </>
  ) : (
    <Button
      isDisabled={true}
      onClick={onClickJoin}
      size="sm"
      colorScheme="gray"
      variant="outline"
    >
      Join
    </Button>
  );
};
JoinButton.groupFragment = gql`
  fragment JoinButton_GroupFragment on Group {
    _id
    name
    currentUserGroupLink {
      _id
    }
    currentUserGroupPermission {
      canJoin
    }
    parentGroup {
      _id
      name
      currentUserGroupLink {
        _id
      }
    }
    subgroups {
      totalCount
    }
    ...useEditCurrentUserGroupMembership_GroupFragment
  }
  ${useEditCurrentUserGroupMembership.groupFragment}
`;
